import Head from 'next/head';
import Link from 'next/link';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PILLARS = [
  {
    tag: 'Helios',
    title: 'Options-market intelligence',
    body: 'Helios is our proprietary engine for reading the options market. It processes market, options and macro data in real time and structures dealer positioning and flow dynamics into signals. Most market data describes what already happened; Helios is built to describe the positioning that shapes what happens next.',
    points: [
      'Positioning — dealer gamma and delta exposure across expirations, resolved into levels that matter intraday',
      'Flow — options order flow and premium dynamics tracked as they arrive',
      'Regime — a continuous read of the trading day\u2019s character: trend, range, exhaustion or turn',
    ],
  },
  {
    tag: 'KTR Institute',
    title: 'Research and intellectual property',
    body: 'Longer-horizon applied research and the intellectual property that supports the broader platform, including advanced signal-processing methods that strengthen the firm\u2019s analytics. The Institute works on questions whose answers arrive in years rather than quarters.',
    points: [
      'Applied signal processing and predictive modelling',
      'A quantum research programme with patents filed and pending',
      'Methods developed here feed the systems the firm operates daily',
    ],
  },
  {
    tag: 'Strandwise',
    title: 'Biotechnology',
    body: 'A private position in Strandwise, a biotechnology company developing implantable biosensor technology for continuous, post-surgical cancer monitoring \u2014 built on a physician-led understanding of the clinical need rather than a thesis assembled from the outside.',
    points: [
      'Implantable, continuous sensing for post-surgical oncology',
      'Longer-horizon private development alongside public-market work',
      'Scientific diligence led from inside the firm',
    ],
  },
];

export default function Platform() {
  return (
    <>
      <Head>
        <title>The Platform &mdash; KTR Capital</title>
        <meta name="description" content="How KTR Capital is built: proprietary market intelligence, an in-house research programme, and biotechnology." />
        <meta name="robots" content="noindex" />
      </Head>
      <Navbar />
      <main className="bg-black text-white min-h-screen">

        <section className="px-6 pt-20 pb-14 border-b border-gray-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">The Platform</div>
            <h1 className="text-4xl md:text-5xl font-light leading-tight tracking-tight mb-6">
              One research platform.<br />Three areas of work.
            </h1>
            <p className="text-lg text-gray-400 font-light leading-relaxed max-w-3xl">
              KTR Capital designs, builds and operates its own research stack. The same infrastructure and the
              same people support everything the firm does &mdash; quantitative market intelligence, applied
              research, and biotechnology. What follows describes how the platform is built.
            </p>
          </div>
        </section>

        <section className="px-6 py-16 border-b border-gray-900">
          <div className="max-w-4xl mx-auto space-y-16">
            {PILLARS.map((p, i) => (
              <div key={p.tag}>
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-xs text-orange-400 font-mono pt-1">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 mb-2">{p.tag}</div>
                    <h2 className="text-2xl md:text-3xl font-light tracking-tight">{p.title}</h2>
                  </div>
                </div>
                <p className="text-base text-gray-400 font-light leading-relaxed max-w-3xl mb-6 md:pl-12">{p.body}</p>
                <ul className="space-y-2.5 md:pl-12">
                  {p.points.map((pt) => (
                    <li key={pt} className="text-sm text-gray-400 font-light leading-relaxed flex gap-3">
                      <span className="text-orange-500 flex-shrink-0">&middot;</span><span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 py-16 border-b border-gray-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Built in-house</div>
            <h2 className="text-2xl md:text-3xl font-light mb-6 tracking-tight">Why we build rather than buy</h2>
            <div className="space-y-5 text-base text-gray-400 font-light leading-relaxed max-w-3xl">
              <p>
                Off-the-shelf analytics are available to everyone, which is precisely why they carry no edge.
                We built our own instruments because we wanted to see the market through tools nobody else was
                using &mdash; and to be able to change them the moment the market changed.
              </p>
              <p>
                The barrier to this work is infrastructure rather than insight: tick-level data across millions
                of contracts, aggregated in real time, is expensive and difficult to operate. That is exactly
                why the capability stays available to a firm willing to build it.
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Evidence</div>
            <h2 className="text-2xl md:text-3xl font-light mb-6 tracking-tight">The engine publishes in public</h2>
            <p className="text-base text-gray-400 font-light leading-relaxed max-w-3xl mb-8">
              The same engine that informs the firm&rsquo;s work publishes a daily market read at
              helios.markets &mdash; issued in the morning, graded against the close that evening, wins and
              misses alike. Nothing is edited after the fact. It is an independent, ongoing record that anyone
              can check without taking our word for it.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/sign-up" className="inline-block bg-orange-500 text-black px-6 py-3 rounded-lg font-medium text-sm hover:bg-orange-600 transition-colors">
                Begin qualification &rarr;
              </Link>
              <Link href="/research" className="inline-block border border-gray-700 text-gray-200 px-6 py-3 rounded-lg font-light text-sm hover:bg-gray-900 transition-colors">
                The research programme
              </Link>
            </div>
            <p className="text-[11px] text-gray-600 font-light mt-10 max-w-3xl leading-relaxed">
              This page is informational and describes the firm and its technology. It is not an offer to sell,
              or a solicitation of an offer to buy, any security. See{' '}
              <Link href="/disclosures" className="text-gray-500 hover:text-orange-400">Investor Disclosures</Link>.
            </p>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
